import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';
import { Theme } from '../theme';

interface AdminProps {
  theme: Theme;
}

interface LinkSubmission {
  id: number;
  link_url: string;
  user_email: string;
  user_name: string;
  submitted_via: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  submission_date: string;
  processed_date?: string;
  extracted_data?: string;
  error_message?: string;
  notes?: string;
}

const AdminContainer = styled.div`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  grid-column: 1 / -1;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 8px;
  }
`;

const AddLinkForm = styled.form`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: ${props => props.theme.background};
  border-radius: 8px;
  border: 1px dashed ${props => props.theme.border};
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px;
    gap: 12px;
  }
`;

const LinkInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}30;
  }
  
  &::placeholder {
    color: ${props => props.theme.subText};
  }
  
  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
    padding: 14px 12px;
    font-size: 16px; /* Prevents iOS zoom */
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 14px 20px;
    font-size: 16px;
  }
`;

const QuickAddHint = styled.div`
  width: 100%;
  font-size: 12px;
  color: ${props => props.theme.subText};
  margin-top: 5px;
`;

const DeleteButton = styled.button`
  padding: 4px 8px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #c82333;
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  margin-bottom: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 20px;
  
  th, td {
    padding: 12px;
    border-bottom: 1px solid ${props => props.theme.border};
    text-align: left;
  }

  th {
    background: ${props => props.theme.tableHeaderBackground};
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  tr:hover {
    background: ${props => props.theme.hoverBackground};
  }
  
  a {
    color: ${props => props.theme.link};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

/* Mobile Card View - shows on mobile only */
const MobileCardList = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MobileCard = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  padding: 14px;
  position: relative;
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const MobileCardLink = styled.a`
  color: ${props => props.theme.link};
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
  flex: 1;
  margin-right: 10px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const MobileCardMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subText};
  margin-bottom: 12px;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 10px;
`;

const MobileProcessButton = styled.button`
  flex: 1;
  padding: 14px 16px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileDeleteButton = styled.button`
  padding: 14px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  
  &:hover {
    background: #c82333;
  }
`;

const Button = styled.button`
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
  font-size: 14px;
  transition: all 0.2s;
`;

const ProcessButton = styled(Button)`
  background-color: ${props => props.theme.primary};
  border: 1px solid ${props => props.theme.primary};
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.85em;
  background: ${props => {
    switch (props.status) {
      case 'processed': return props.theme.successBackground;
      case 'processing': return '#ffa500';
      case 'failed': return props.theme.failedBackground;
      default: return props.theme.pendingBackground;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'processed': return props.theme.successText;
      case 'processing': return 'white';
      case 'failed': return props.theme.failedText;
      default: return props.theme.pendingText;
    }
  }};
  border: 1px solid currentColor;
`;

const LinkSubmissionsAdmin: React.FC<AdminProps> = ({ theme }) => {
  const [submissions, setSubmissions] = useState<LinkSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Set<number>>(new Set());
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;
    
    setIsAdding(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const response = await fetch(`${apiBase}/api/link-submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ link_url: newLinkUrl.trim() })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewLinkUrl('');
        setSuccessMessage('✅ Link added! Click "Process Tweet" to extract prediction data.');
        await fetchSubmissions();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(data.error || 'Failed to add link');
      }
    } catch (err: any) {
      setError(`Failed to add link: ${err.message}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this link submission?')) return;
    
    try {
      const response = await fetch(`${apiBase}/api/link-submissions/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchSubmissions();
      } else {
        setError('Failed to delete link');
      }
    } catch (err: any) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      console.log('apiBase:', apiBase);
      console.log('Full URL:', `${apiBase}/api/link-submissions`);
      
      // Try using fetch instead of axios
      const response = await fetch(`${apiBase}/api/link-submissions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setSubmissions(data);
        setError(null);
      } else {
        console.error('Unexpected response format:', data);
        setError('Unexpected response format from server');
      }
    } catch (err: any) {
      console.error('Error fetching link submissions:', err);
      setError(`Failed to fetch link submissions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (id: number) => {
    try {
      setProcessing(prev => new Set(prev.add(id)));
      
      const response = await axios.post(`${apiBase}/api/link-submissions/${id}/process`);
      
      // Refresh the list
      await fetchSubmissions();
      
      // Show detailed success message from backend
      if (response.data?.message) {
        alert(`${response.data.message}\n\n📋 Check the "Pending" predictions tab to review and approve the extracted data.`);
      } else {
        alert('Link processed successfully! Check the Prediction Submissions tab for review.');
      }
    } catch (err: any) {
      console.error('Error processing link:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      alert(`❌ Failed to process link: ${errorMessage}`);
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <AdminContainer theme={theme}>
      <Title theme={theme}>🔗 Link Submissions</Title>
      
      {/* Quick Add Link Form */}
      <AddLinkForm onSubmit={handleAddLink} theme={theme}>
        <LinkInput
          type="url"
          value={newLinkUrl}
          onChange={(e) => setNewLinkUrl(e.target.value)}
          placeholder="Paste Twitter/X.com URL here (e.g., https://x.com/saylor/status/...)"
          theme={theme}
          disabled={isAdding}
        />
        <AddButton type="submit" disabled={isAdding || !newLinkUrl.trim()} theme={theme}>
          {isAdding ? '⏳ Adding...' : '➕ Add Link'}
        </AddButton>
        <QuickAddHint theme={theme}>
          💡 Paste a tweet URL → Click "Add Link" → Click "Process Tweet" to extract prediction with AI
        </QuickAddHint>
      </AddLinkForm>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          padding: '12px', 
          background: 'rgba(40, 167, 69, 0.2)', 
          border: '1px solid #28a745',
          borderRadius: '6px',
          marginBottom: '15px',
          color: '#28a745'
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          padding: '12px', 
          background: 'rgba(220, 53, 69, 0.2)', 
          border: '1px solid #dc3545',
          borderRadius: '6px',
          marginBottom: '15px',
          color: '#dc3545'
        }}>
          ❌ {error}
        </div>
      )}
      
      {loading ? (
        <p>Loading link submissions...</p>
      ) : submissions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.subText }}>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>📭 No link submissions yet</p>
          <p>Paste a Twitter/X URL above to get started!</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <MobileCardList theme={theme}>
            {submissions.map(submission => (
              <MobileCard key={submission.id} theme={theme}>
                <MobileCardHeader>
                  <MobileCardLink 
                    href={submission.link_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    theme={theme}
                  >
                    {truncateUrl(submission.link_url, 45)}
                  </MobileCardLink>
                  <StatusBadge status={submission.status} theme={theme}>
                    {submission.status}
                  </StatusBadge>
                </MobileCardHeader>
                <MobileCardMeta theme={theme}>
                  #{submission.id} • {new Date(submission.submission_date).toLocaleDateString()}
                  {submission.error_message && (
                    <span style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                      ⚠️ {submission.error_message}
                    </span>
                  )}
                </MobileCardMeta>
                <MobileCardActions>
                  {(submission.status === 'pending' || submission.status === 'failed') && (
                    <>
                      <MobileProcessButton 
                        onClick={() => handleProcess(submission.id)} 
                        theme={theme}
                        disabled={processing.has(submission.id)}
                      >
                        {processing.has(submission.id) ? '⏳ Processing...' : '🤖 Process Tweet'}
                      </MobileProcessButton>
                      <MobileDeleteButton onClick={() => handleDelete(submission.id)}>
                        🗑️
                      </MobileDeleteButton>
                    </>
                  )}
                  {submission.status === 'processed' && (
                    <span style={{ color: '#28a745', padding: '14px', fontSize: '15px' }}>✅ Processed</span>
                  )}
                </MobileCardActions>
              </MobileCard>
            ))}
          </MobileCardList>
          
          {/* Desktop Table View */}
          <Table theme={theme}>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>ID</th>
              <th>Twitter Link</th>
              <th style={{ width: '120px' }}>Source</th>
              <th style={{ width: '150px' }}>Submitted</th>
              <th style={{ width: '100px' }}>Status</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map(submission => (
              <tr key={submission.id}>
                <td>{submission.id}</td>
                <td>
                  <a href={submission.link_url} target="_blank" rel="noopener noreferrer">
                    {truncateUrl(submission.link_url, 60)}
                  </a>
                </td>
                <td>
                  <span style={{ 
                    fontSize: '11px', 
                    padding: '2px 6px', 
                    background: submission.submitted_via === 'admin-dashboard' ? '#007bff20' : '#6c757d20',
                    borderRadius: '4px'
                  }}>
                    {submission.submitted_via || 'unknown'}
                  </span>
                  {submission.user_name && submission.user_name !== 'Admin' && (
                    <div><small style={{ color: theme.subText }}>{submission.user_name}</small></div>
                  )}
                </td>
                <td style={{ fontSize: '12px' }}>{new Date(submission.submission_date).toLocaleString()}</td>
                <td>
                  <StatusBadge status={submission.status} theme={theme}>
                    {submission.status}
                  </StatusBadge>
                  {submission.error_message && (
                    <div><small style={{ color: theme.failedText }}>{submission.error_message}</small></div>
                  )}
                </td>
                <td>
                  {submission.status === 'pending' && (
                    <>
                      <ProcessButton 
                        onClick={() => handleProcess(submission.id)} 
                        theme={theme}
                        disabled={processing.has(submission.id)}
                        style={{ marginRight: '5px' }}
                      >
                        {processing.has(submission.id) ? '⏳...' : '🤖 Process'}
                      </ProcessButton>
                      <DeleteButton onClick={() => handleDelete(submission.id)}>
                        🗑️
                      </DeleteButton>
                    </>
                  )}
                  {submission.status === 'processed' && (
                    <span style={{ color: '#28a745', fontSize: '12px' }}>✅ Done</span>
                  )}
                  {submission.status === 'failed' && (
                    <>
                      <ProcessButton 
                        onClick={() => handleProcess(submission.id)} 
                        theme={theme}
                        disabled={processing.has(submission.id)}
                        style={{ marginRight: '5px' }}
                      >
                        🔄 Retry
                      </ProcessButton>
                      <DeleteButton onClick={() => handleDelete(submission.id)}>
                        🗑️
                      </DeleteButton>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </>
      )}
    </AdminContainer>
  );
};

export default LinkSubmissionsAdmin;